import React, { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { ArrowRight, Globe, Package, Handshake, Sparkles } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { api, getMl } from "@/lib/api";
import WorldMap from "@/components/WorldMap";
import Counter from "@/components/Counter";
import BrandFamily from "@/components/BrandFamily";

const Home = () => {
  const { t, lang } = useI18n();
  const { siteSettings } = useOutletContext() || {};
  const [exports, setExports] = useState([]);
  const [importCats, setImportCats] = useState([]);
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get("/products?type=export&featured=true").then((r) => setExports(r.data.slice(0, 6)));
    api.get("/categories?type=import").then((r) => setImportCats(r.data));
    api.get("/services").then((r) => setServices(r.data.slice(0, 6)));
  }, []);

  const counters = siteSettings?.homepage?.counters || { countries: 35, categories: 60, partners: 120, solutions: 12 };
  const partnerBrands = siteSettings?.homepage?.partner_brands || [];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative bg-[#0A1128] text-white overflow-hidden min-h-[90vh] flex items-center">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(135deg, rgba(10,17,40,0.92) 0%, rgba(10,17,40,0.7) 60%, rgba(10,17,40,0.5) 100%), url('https://images.pexels.com/photos/32119533/pexels-photo-32119533.jpeg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 grain-overlay opacity-30 pointer-events-none" />

        <div className="container-nakia relative z-10 py-32 md:py-40 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="flex items-center gap-3 mb-6">
              <span className="gold-divider" />
              <span className="text-eyebrow text-[#D4AF37]">{t("hero.eyebrow")}</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.05] tracking-tight mb-8">
              {t("hero.title")}
              <br />
              <span className="text-[#D4AF37]">{t("hero.titleAccent")}</span>
            </h1>
            <p className="text-base sm:text-lg text-white/80 max-w-xl mb-10 leading-relaxed">
              {t("hero.subtitle")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/export-commodities" data-testid="hero-cta-explore" className="btn-gold">
                {t("hero.cta1")} <ArrowRight size={16} />
              </Link>
              <Link to="/contact" data-testid="hero-cta-quote" className="btn-outline-gold">
                {t("hero.cta2")}
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 hidden lg:block">
            <WorldMap />
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent" />
      </section>

      {/* COUNTERS */}
      <section className="bg-[#0A1128] text-white py-20 border-t border-white/5" data-testid="counters-section">
        <div className="container-nakia grid grid-cols-2 md:grid-cols-4 gap-10 text-center">
          {[
            { v: counters.countries, l: t("counters.countries"), s: "+" },
            { v: counters.categories, l: t("counters.categories"), s: "+" },
            { v: counters.partners, l: t("counters.partners"), s: "+" },
            { v: counters.solutions, l: t("counters.solutions"), s: "" },
          ].map((c, i) => (
            <div key={i} data-testid={`counter-${i}`}>
              <div className="font-display text-5xl md:text-6xl text-[#D4AF37] mb-3">
                <Counter end={c.v} suffix={c.s} />
              </div>
              <div className="text-xs uppercase tracking-[0.25em] text-white/60">{c.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED EXPORTS */}
      <section className="py-24 sm:py-32 bg-[#F8FAFC]" data-testid="featured-exports-section">
        <div className="container-nakia">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="h-px w-10 bg-[#D4AF37]" />
                <span className="text-eyebrow text-[#D4AF37]">{t("common.featured")}</span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0A1128] leading-tight max-w-2xl">
                {t("sections.featuredExports")}
              </h2>
              <p className="text-slate-600 mt-4 max-w-xl">{t("sections.featuredExportsSub")}</p>
            </div>
            <Link to="/export-commodities" data-testid="featured-view-all" className="btn-outline-navy self-start md:self-auto">
              {t("common.viewAll")} <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {exports.map((p) => (
              <Link
                key={p.id}
                to={`/products/${p.id}`}
                data-testid={`product-card-${p.id}`}
                className="group card-light"
              >
                <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                  {p.images?.[0] && (
                    <img
                      src={p.images[0]}
                      alt={getMl(p.name, lang)}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  )}
                </div>
                <div className="p-7">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-3">{p.origin_country}</div>
                  <h3 className="font-display text-xl text-[#0A1128] mb-2 group-hover:text-[#D4AF37] transition-colors">
                    {getMl(p.name, lang)}
                  </h3>
                  <p className="text-sm text-slate-600 line-clamp-2">{getMl(p.short_description, lang)}</p>
                  <div className="mt-5 flex items-center justify-between text-xs">
                    <span className="text-slate-500">MOQ: {p.moq || "—"}</span>
                    <span className="text-[#D4AF37] uppercase tracking-[0.2em] font-semibold">View →</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* IMPORT CATEGORIES */}
      <section className="py-24 sm:py-32 bg-[#0A1128] text-white relative overflow-hidden" data-testid="import-cats-section">
        <div className="absolute inset-0 grain-overlay opacity-15" />
        <div className="container-nakia relative">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="h-px w-10 bg-[#D4AF37]" />
                <span className="text-eyebrow text-[#D4AF37]">Import</span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-2xl">
                {t("sections.importCats")}
              </h2>
              <p className="text-white/70 mt-4 max-w-xl">{t("sections.importCatsSub")}</p>
            </div>
            <Link to="/import-products" data-testid="import-view-all" className="btn-outline-gold self-start md:self-auto">
              {t("common.viewAll")} <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {importCats.map((c) => (
              <Link
                key={c.id}
                to={`/import-products?cat=${c.slug}`}
                data-testid={`import-cat-${c.slug}`}
                className="card-dark p-6 flex flex-col items-start gap-3 hover:bg-[#1F2A44]"
              >
                <Package size={22} className="text-[#D4AF37]" />
                <h3 className="font-display text-lg leading-tight">{getMl(c.name, lang)}</h3>
                <span className="mt-auto text-[#D4AF37] text-xs uppercase tracking-[0.2em]">→</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* BRAND FAMILY (Sister Brands like NakiaQurbani) */}
      <BrandFamily brands={partnerBrands} />

      {/* SERVICES */}
      <section className="py-24 sm:py-32 bg-white" data-testid="services-section">
        <div className="container-nakia">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="h-px w-10 bg-[#D4AF37]" />
              <span className="text-eyebrow text-[#D4AF37]">Solutions</span>
              <span className="h-px w-10 bg-[#D4AF37]" />
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0A1128] leading-tight">
              {t("sections.services")}
            </h2>
            <p className="text-slate-600 mt-4">{t("sections.servicesSub")}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s) => (
              <div key={s.id} data-testid={`service-card-${s.id}`} className="card-light p-8">
                <div className="w-12 h-12 border border-[#D4AF37]/40 flex items-center justify-center rounded-sm mb-5">
                  <Sparkles size={20} className="text-[#D4AF37]" />
                </div>
                <h3 className="font-display text-xl text-[#0A1128] mb-3">{getMl(s.name, lang)}</h3>
                <p className="text-sm text-slate-600">{getMl(s.short_description, lang)}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services" data-testid="services-view-all" className="btn-outline-navy">
              {t("common.viewAll")} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A1128] text-white py-20 border-t border-white/5" data-testid="cta-section">
        <div className="container-nakia text-center max-w-3xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-5">{t("sections.cta")}</h2>
          <p className="text-white/75 mb-8">{t("sections.ctaSub")}</p>
          <Link to="/contact" data-testid="cta-contact" className="btn-gold">
            {t("common.requestQuote")} <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
